import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import enGB from "date-fns/locale/en-GB";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./EventsCalendar.css";
import { AppContext } from "../../../context/AppContext";
import Utils from "../../../utils/Utils";

import { toast } from "react-toastify";
import ViewEvents from "./ViewEvents";

export const EVENT_TYPES = [
  { value: "birthday", label: "Birthday", tone: "success" },
  { value: "wedding", label: "Wedding", tone: "success" },
  { value: "engagement", label: "Engagement", tone: "success" },
  { value: "anniversary", label: "Anniversary", tone: "success" },

  { value: "conference", label: "Conference", tone: "info" },
  { value: "seminar", label: "Seminar", tone: "info" },
  { value: "meeting", label: "Meeting", tone: "info" },

  { value: "party", label: "Party", tone: "danger" },
  { value: "concert", label: "Concert", tone: "danger" },

  { value: "festival", label: "Festival", tone: "warning" },
  { value: "other", label: "Other", tone: "default" },
];

export const TONE_STYLES = {
  success: { bg: "#dcfce7", text: "#166534", border: "#86efac" }, // green
  danger: { bg: "#fee2e2", text: "#7f1d1d", border: "#fecaca" }, // red
  warning: { bg: "#fef3c7", text: "#7a5200", border: "#fde68a" }, // amber
  info: { bg: "#dbeafe", text: "#1e40af", border: "#bfdbfe" }, // blue
  default: { bg: "#eef2f7", text: "#1f2937", border: "#e5e7eb" }, // grey
};

// date-fns localizer
const locales = { "en-GB": enGB };
const localizer = dateFnsLocalizer({
  format,
  parse: (str, fmt, refDate) => parse(str, fmt, refDate, { locale: enGB }),
  startOfWeek: () => startOfWeek(new Date(), { locale: enGB }),
  getDay,
  locales,
});

// Small helpers
const fmtShort = (d) =>
  d
    ? new Date(d).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "—";
const fmtDateOnly = (d) =>
  d ? new Date(d).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—";
const fmtTimeOnly = (d) =>
  d ? new Date(d).toLocaleTimeString(undefined, { timeStyle: "short" }) : "—";

export default function EventsCalendar({
  selected,
  setSelected,
  isEditing,
  setIsEditing,
}) {
  const { eventsList, menuItems } = useContext(AppContext);

  const [events, setEvents] = useState([]);

  const handleEventsList = useCallback(() => {
    const data = (eventsList || []).map((e) => ({
      ...e,
      id: String(e.event_id),
      title: e.event_title,
      start: new Date(e.event_start),
      end: new Date(e.event_end),
      allDay: false,
    }));
    setEvents(data);
  }, [eventsList]);

  useEffect(() => {
    if (
      !eventsList ||
      (eventsList && !Array.isArray(eventsList)) ||
      eventsList.length == 0
    )
      return;
    handleEventsList();
  }, [eventsList]);

  // Banner limited content (title + date range)
  const EventBanner = ({ event }) => (
    <div className="fp-event-content">
      <div className="fp-event-title">{event.event_title}</div>
      <div className="fp-event-sub">
        {fmtDateOnly(event.start)}
        {event.end ? ` – ${fmtDateOnly(event.end)}` : ""}
      </div>
    </div>
  );

  const getTypeMeta = (type) =>
    EVENT_TYPES.find((t) => t.value === type) ?? {
      tone: "default",
      label: "Other",
    };

  const getStyleForType = (type) => {
    const tone = getTypeMeta(type).tone;
    const s = TONE_STYLES[tone] ?? TONE_STYLES.default;
    return {
      backgroundColor: s.bg,
      border: `1px solid ${s.border}`,
      color: s.text,
    };
  };

  // Colour the banner based on event_type
  const eventPropGetter = (event) => {
    const style = getStyleForType(event.event_type);
    return { style };
  };

  const handleEvents = (events) => {
    const menuDetails =
      menuItems &&
      menuItems.length != 0 &&
      menuItems.find((menu) => menu.id == events.menu_id);

    events.menu = menuDetails ?? null;
    // delete events.menu_id;

    setSelected({
      ...events,
      event_title: events.event_title ?? events.title,
      event_start:
        typeof events.event_start === "string"
          ? events.event_start
          : events.start
          ? events.start.toISOString()
          : null,
      event_end:
        typeof events.event_end === "string"
          ? events.event_end
          : events.end
          ? events.end.toISOString()
          : null,
    });
  };

  return (
    <div className={`events-flex ${selected ? "has-details" : ""}`}>
      {/* Calendar column */}
      <div className="calendar-col">
        <div className="card h-100 p-3 radius-12">
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            views={["month", "week", "day"]}
            defaultView="month"
            style={{ height: "auto", minHeight: 600 }}
            components={{ event: EventBanner }}
            eventPropGetter={eventPropGetter}
            onSelectEvent={(event) => handleEvents(event)}
            popup
          />
        </div>
      </div>

      {/* Slide-in details pane (no modal) */}
      <ViewEvents
        fmtShort={fmtShort}
        setIsEditing={setIsEditing}
        isEditing={isEditing}
        setSelected={setSelected}
        selected={selected}
        handleEventsList={handleEventsList}
      />
    </div>
  );
}
